import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import {
	appendOptimisticChatMessage,
	markChatMessageFailed,
} from "web_myplann/Chat/Helpers/Messages/chatMessagesCache";
import { confirmOptimisticChatMessage } from "web_myplann/Chat/Helpers/Messages/confirmOptimisticChatMessage";
import { createOptimisticChatMessage } from "web_myplann/Chat/Helpers/Messages/createOptimisticChatMessage";
import { mapChatMessage } from "web_myplann/Chat/Helpers/Messages/mapChatMessage";
import {
	postChatsConversationsIdMessagesV2,
	type SendMessageRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type SendChatMessageVariables = {
	conversationId: number;
	content: string;
	clientId: string;
	file?: File;
};

export const useSendChatMessageMutation = (conversationId: number | null) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ChatKeys.send(conversationId),
		scope: { id: ChatKeys.sendScope(conversationId) },
		mutationFn: async ({
			conversationId,
			content,
			file,
		}: SendChatMessageVariables) => {
			const message = await processResponse(
				(request: SendMessageRequestDTO) =>
					postChatsConversationsIdMessagesV2(request, conversationId),
				{ skipToastFor: true },
			)(
				file
					? { attachment: file, content, attachmentTitle: file.name }
					: { content },
			);

			if (!message) {
				throw new Error("Chat message could not be sent");
			}

			return mapChatMessage(message);
		},
		onMutate: ({ conversationId, content, clientId, file }) => {
			appendOptimisticChatMessage(
				queryClient,
				conversationId,
				createOptimisticChatMessage({
					clientId,
					content,
					file,
					localAttachmentUrl: file ? URL.createObjectURL(file) : undefined,
				}),
			);
		},
		onError: (_error, { conversationId, clientId }) => {
			markChatMessageFailed(queryClient, conversationId, clientId);
		},
		onSuccess: (message, { conversationId, clientId, file }) => {
			const attachmentId = message.attachments[0]?.id;
			if (file && attachmentId !== undefined) {
				queryClient.setQueryData(ChatKeys.attachment(attachmentId), file);
			}

			confirmOptimisticChatMessage(
				queryClient,
				conversationId,
				clientId,
				message,
			);

			queryClient.invalidateQueries({ queryKey: ChatKeys.list() });
		},
	});
};
